import * as React from 'react'
import classNames from 'classnames'
import styled from 'styled-components'
import { tuple } from '../_type/type'
import { cloneElement } from '../_util/reactNode'
import IconSpin from '../_assets/IconSpin'

const Wrapper = styled.button`
  /* 清除默认样式 */
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  &:focus {
    outline: none;
  }
  /* global */
  padding: 0 16px;
  width: 100%;
  min-width: 120px;
  height: 40px;
  border-radius: 40px;
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  span {
    display: inline-block;
    white-space: nowrap;
    font-size: 16px;
    font-weight: 700;
  }
  &:not(.loading, .disabled):hover {
    opacity: 0.8;
  }
  /* base */
  &.primary {
    background: #f4c470;
    span {
      color: #000;
      line-height: 40px;
    }
  }
  &.second {
    background: rgba(255, 255, 255, 0.12);
    span {
      color: rgba(255, 255, 255, 0.85);
      line-height: 40px;
    }
  }
  &.solid {
    border: 1px solid #6c4f1c;
    span {
      color: #f4c470;
      line-height: 38px;
    }
  }
  &.text {
    span {
      color: rgba(255, 255, 255, 0.85);
    }
  }
  &.danger {
    background: rgba(238, 105, 41, 0.1);
    span {
      color: #ee6929;
    }
    &:not(.loading, .disabled):hover {
      background: rgba(238, 105, 41, 0.2);
    }
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
  &.loading {
    cursor: not-allowed;
    svg {
      margin-right: 6px;
    }
  }
`

const ButtonTypes = tuple('primary', 'second', 'solid', 'text', 'danger')

type ButtonType = (typeof ButtonTypes)[number]

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'prefix' | 'suffix'> {
  className?: string
  type?: ButtonType
  loading?: boolean
  loadColor?: string
  disabled?: boolean
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  children?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLElement>
}

const Button: React.FC<ButtonProps> = React.forwardRef((props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
  const {
    className,
    type = 'primary',
    loading = false,
    loadColor,
    disabled = false,
    prefix,
    suffix,
    children,
    onClick,
    ...rest
  } = props

  const classes = classNames(className, 'flex flex-row items-center justify-center', {
    [`${type}`]: type,
    loading,
    disabled,
  })

  const loadingColor = React.useMemo(() => {
    switch (type) {
      case 'primary':
        return '#000000'
      case 'danger':
        return '#ee6929'
      default:
        return 'rgba(255, 255, 255, 0.85)'
    }
  }, [type])

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    if (loading || disabled) {
      e.preventDefault()
      return
    }
    onClick?.(e)
  }

  return (
    <Wrapper className={classes} ref={ref} onClick={handleClick} {...rest}>
      {prefix}
      {!disabled && loading && <IconSpin color={loadColor ?? loadingColor} />}
      <span>{cloneElement(children)}</span>
      {suffix}
    </Wrapper>
  )
})

export default Button
